import type { LegacyRef } from 'react';
import { useRef, useState } from 'react';
import { PAGE_POSITIONS } from 'types';
import type {
  CapiProductPage,
  ProductStockListing,
  Raffle
} from 'types/capi/pdp';

import HorizontalLine from '~/components/HorizontalLine';
import SizeSelector from '~/components/Pdp/WhereToBuy/SizeSelector';
import StickyCta from '~/components/Pdp/WhereToBuy/StickyCta';
import StockListing, {
  StockListingButtons
} from '~/components/Pdp/WhereToBuy/StockListing';
import Title from '~/components/Pdp/WhereToBuy/Title';
import { pdpSizes } from '~/domain/sizes';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';
import gtm from '~/services/gtm.client';
import {
  getSortedStockListingsForSize,
  getStockStatusForSize
} from '~/utils/listings';

import RaffleListing from './RaffleListing';
import ViewToggle from './ViewToggle';

export enum SelectedView {
  RAFFLES,
  RETAILERS
}

export default function WhereToBuy() {
  const product = usePageData<CapiProductPage>();
  const { raffle } = useQsParams();

  const hasRaffles = product.stock_listings.raffles.length > 0;

  const firstListingRef = useRef<HTMLDivElement>(null);

  const [selectedView, setSelectedView] = useState(
    hasRaffles && raffle ? SelectedView.RAFFLES : SelectedView.RETAILERS
  );
  const [selectedSize, setSelectedSize] = useState(useQsParams().size);
  const [showSizeGrid, setShowSizeGrid] = useState(false);

  const stockListings = getSortedStockListingsForSize(product, selectedSize);
  const stockStatus = getStockStatusForSize(stockListings[0], selectedSize);

  const sizeSelectorRef = useRef<HTMLDivElement>(null);

  function onSizeSelectorClick() {
    sizeSelectorRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowSizeGrid(true);
    gtm.sendUserInteractionEvent('select size', PAGE_POSITIONS.stickyCta);
  }

  function trackBuyNowClick() {
    gtm.sendUserInteractionEvent('buy now', PAGE_POSITIONS.stickyCta);
  }

  return (
    <>
      <div className="flex w-full flex-col gap-4 md:col-span-full lg:col-span-4">
        {selectedView === SelectedView.RETAILERS &&
          product.allow_size_selection && (
            <div ref={sizeSelectorRef}>
              <SizeSelector
                sizes={pdpSizes}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
                showSizeGrid={showSizeGrid}
                setShowSizeGrid={setShowSizeGrid}
              />
            </div>
          )}

        <div>
          <Title text="Where to buy" />
          <div className="w-full border-x border-concrete bg-white">
            {hasRaffles && (
              <>
                <ViewToggle
                  selectedView={selectedView}
                  setSelectedView={setSelectedView}
                />
                <HorizontalLine />
              </>
            )}

            {selectedView === SelectedView.RETAILERS && (
              <StockListings
                selectedSize={selectedSize}
                firstListingRef={firstListingRef}
                listings={stockListings}
              />
            )}

            {selectedView === SelectedView.RAFFLES && (
              <Raffles raffles={product.stock_listings.raffles} />
            )}
          </div>
        </div>
      </div>
      {stockListings.length > 0 && (
        <StickyCta
          mainBuyNowButtonRef={firstListingRef}
          selectedSize={selectedSize}
          stockStatus={stockStatus}
          onSizeSelectorClick={onSizeSelectorClick}
        >
          <StockListingButtons
            stockStatus={stockStatus}
            selectedSize={selectedSize}
            listing={stockListings[0] as ProductStockListing}
            position={0}
            onBuyNowClick={trackBuyNowClick}
            showAfffiliateLogo
          />
        </StickyCta>
      )}
    </>
  );
}

const StockListings = ({
  listings,
  selectedSize,
  firstListingRef
}: {
  firstListingRef: LegacyRef<HTMLDivElement>;
  listings: ProductStockListing[];
  selectedSize?: string;
}) => {
  return (
    <>
      {listings.map((listing, index) => (
        <div key={listing.affiliate.name}>
          <StockListing
            key={listing.affiliate.name}
            buttonsRef={
              (index === 0
                ? firstListingRef
                : () => {}) as LegacyRef<HTMLDivElement>
            }
            position={index}
            listing={listing}
            selectedSize={selectedSize}
          />
          <HorizontalLine />
        </div>
      ))}
    </>
  );
};

const Raffles = ({ raffles }: { raffles: Raffle[] }) => {
  return (
    <>
      {raffles.map((raffle, index) => (
        <div key={raffle.id}>
          <RaffleListing raffleListing={raffle} buttonPosition={index} />
          <HorizontalLine />
        </div>
      ))}
    </>
  );
};
